import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, ReplaySubject, Subject, map, switchAll, switchMap, take, tap, throwError } from "rxjs";
import { environment } from "src/environments/environment";
import { Employee } from "../models/employee.model";
import { ActivatedRoute, ParamMap } from "@angular/router";
import { SelectionItem } from "../models/selection-item.model";

@Injectable({
  providedIn: 'root'
})
export class EmployeeService {

  private filterStream = new BehaviorSubject<any>(null);
  filterStream$ = this.filterStream.asObservable();

  private employees:Subject<Employee[]> = new ReplaySubject<Employee[]>(1);
  employees$ = this.employees.asObservable();

  constructor( private http: HttpClient, private route: ActivatedRoute ) { }

  addFilter(filter: any) {
    this.filterStream.next(filter);
    this.getEmployeesByStream();
  }


  addEmployees( employees: Array<Employee> ) {
    this.employees.next( employees );
  }

  getEmployeeBySlug$(slug: string | null) {
    const expand = 'selectionItems,locations';

    if (!slug) {
      const err = new Error('Invalid slug provided');
      throwError(() => err);
    }

    let url = environment.apiUrl + '/providers/' + environment.providerId + '/employees?slug=' + slug + '&expand=' + expand ;

    const responseToModel = (response: any): any => {
      //console.log(response);
      if (response._embedded && response._embedded.employee && response._embedded.employee[0]) {
        return new Employee(response._embedded.employee[0]);
      }
    }

    return this.http.get(url).pipe(
      map(response => responseToModel(response))
    );
  }

  getEmployeeByPageRoute$(params$: Observable<ParamMap>) {
    return params$.pipe(
      map(params => {
        if (params.has('employeeSlug')) return params.get('employeeSlug');
        return null;
      }),
      switchMap(employeeSlug => this.getEmployeeBySlug$(employeeSlug))
    );
  }

  getAllEmployeeServices$() {
    const expand = 'selectionItems';
    let url = environment.apiUrl + '/providers/' + environment.providerId + '/employees?expand=' + expand + '&pageSize=100';

    const responseToModel = (response: any): any => {

      if (response._embedded && response._embedded.employee) {
        return response._embedded.employee.map((employee: any) => {
          return employee.selectionItems[0].title;
        }).filter((value: any, index: any, self: string | any[]) => self.indexOf(value) === index);
      } else {
        return [];
      }
    }

    return this.http.get(url).pipe(
      map(response => responseToModel(response)),
    );

  }

  quasirandom() {
    const randomFields = ['color','created','email','id','updated','phone'];
    return randomFields[Math.floor(Math.random() * randomFields.length)];
  }

  quasiSortOrder() {
    const sortOrders = ['asc','desc'];
    return sortOrders[Math.floor(Math.random() * sortOrders.length)];
  }

  getEmployees$(pageSize: number = 100, page: number = 1, sortBy: string = 'name', sortOrder: string = 'none', location: string | null = null, service: string | null = null) {
    const expand = 'selectionItems,locations';
    let url = environment.apiUrl + '/providers/' + environment.providerId + '/employees?expand=' + expand;
    url = url + ((pageSize) ? '&pageSize=' + pageSize : '');
    url = url+ ((page) ? '&page=' + page : '');
    if (sortOrder == 'none') {
      url = url+ ((sortBy) ? '&sortBy=' + this.quasirandom() : '');
      url = url+ ((sortOrder) ? '&sortOrder=' + this.quasiSortOrder() : '');
    } else if (sortOrder == 'asc' || sortOrder == 'desc') {
      url = url+ ((sortBy) ? '&sortBy=' + sortBy : '');
      url = url+ ((sortOrder) ? '&sortOrder=' + sortOrder : '');
    }

    //url = url+ ((location) ? '&location=' + location : '');
    //url = url+ ((service) ? '&service=' + service : '');

    const responseToModel = (response: any): any => {
      if (response._embedded && response._embedded.employee) {
        if (!!location || !!service) {
          let employees = response._embedded.employee.filter((e:Employee) => e.public)
          if (!!location) {
            employees = employees.filter((employee: Employee) => {
              return employee.selectionItems?.some((selectionItem: SelectionItem) => {
                return selectionItem?.locations?.some(selection => location == selection.id);
              });
            });
          }
          if (!!service) {
            employees = employees.filter((employee: Employee) => {
              return employee.selectionItems?.some((selectionItem: SelectionItem) => {
                return service == selectionItem?.title;
              });
            });
          }
          return employees.map((employee: any) => new Employee(employee));
        } else {
          // return response._embedded.employee.map((employee: any) => new Employee(employee));
          return response._embedded.employee.filter((e:Employee) => e.public).map((employee: any) => new Employee(employee));
        }
      } else {
        return [];
      }
    }

    return this.http.get(url).pipe(
      map(response => responseToModel(response)),
      //tap(console.dir)
    );

  }

  getEmployeesByPageRoute$(params$: Observable<ParamMap>, queryParams$: Observable<ParamMap>) {

    let pageSize = 100;
    let page = 1;
    let sortBy = 'name';
    let sortOrder = 'none';
    let location: string | null | undefined =  undefined;
    let service: string | null | undefined =  undefined;

    const setParameter = ( params: any ): any => {
      //console.dir(params);
      if (params.has('pageSize')) {
        pageSize = params.get('pageSize');
      }
      if (params.has('page')) {
        page = params.get('page');
      }
      if (params.has('sortBy')) {
        sortBy = params.get('sortBy');
      }
      if (params.has('sortOrder')) {
        sortOrder = params.get('sortOrder');
      }
      if (params.has('location')) {
        location = params.get('location');
      }
      if (params.has('service')) {
        service = params.get('service');
      }
    }

    return queryParams$.pipe(
      map(params => setParameter(params)),
      switchMap(employees => this.getEmployees$(pageSize, page, sortBy, sortOrder, location, service))
    )
  }


  public getFilterStream$(): Observable<any> {
    return this.filterStream;
  }

  public getFilteredEmployees$(): Observable<Array<Employee>> {
    return this.employees$;
  }

  private getEmployeesByStream() {

    let pageSize = 100;
    let page = 1;
    let sortBy = 'name';
    let sortOrder = 'asc';
    let location: string | null | undefined =  undefined;
    let service: string | null | undefined =  undefined;

    const parseCondition = (condition: any) : any => {
      if (condition.sortBy) {
        sortBy = condition.sortBy;
      }
      if (condition.sortOrder) {
        sortOrder = condition.sortOrder;
      }
      if (condition.location) {
        location = condition.location;
      }
      if (condition.service) {
        service = condition.service;
      }
    }


    this.filterStream$.pipe(
      take(1),
      //tap(console.log),
      map(condition => parseCondition(condition)),
      switchMap(employees => this.getEmployees$(pageSize, page, sortBy, sortOrder, location, service))
    ).subscribe(result => this.addEmployees(result));
  }
}